import { useMemo } from "react";
import { NestedCSSProperties } from "typestyle/lib/types";
import { style as typestyle } from "typestyle";

import "./Typography.css";
import { BaseElementProps } from "./Base";

// Define specific size types for each variant
type HeadingSize = "5xl" | "4xl" | "3xl" | "2xl" | "xl" | "lg";
type BodySize = "xl" | "lg" | "base" | "sm" | "xs";
type LabelSize = "link" | "link-lg" | "button" | "subheading";

// Define the props for each variant using a discriminated union
export type TypographyProps =
    | { onClick?: () => void; variant: "heading"; size: HeadingSize }
    | { onClick?: () => void; variant: "body"; size: BodySize }
    | { onClick?: () => void; variant: "label"; size: LabelSize };

const Typography: React.FC<
    TypographyProps & { sx?: NestedCSSProperties; onMouseOver?: () => void } & BaseElementProps<HTMLElement>
> = ({ sx, variant, size, onClick, children, ...baseProps }) => {
    const typographyStyles = useMemo(() => {
        return typestyle({ ...sx, pointerEvents: onClick ? undefined : "none" });
    }, [onClick, sx]);

    if (variant === "heading") {
        switch (size) {
            case "5xl":
                return (
                    <h1 {...baseProps} className={`heading-5xl ${typographyStyles}`} onClick={onClick}>
                        {children}
                    </h1>
                );
            case "4xl":
                return (
                    <h2 {...baseProps} className={`heading-4xl ${typographyStyles}`} onClick={onClick}>
                        {children}
                    </h2>
                );
            case "3xl":
                return (
                    <h3 {...baseProps} className={`heading-3xl ${typographyStyles} `} onClick={onClick}>
                        {children}
                    </h3>
                );
            case "2xl":
                return (
                    <h4 {...baseProps} className={`heading-2xl ${typographyStyles}`} onClick={onClick}>
                        {children}
                    </h4>
                );
            case "xl":
                return (
                    <h5 {...baseProps} className={`heading-xl ${typographyStyles}`} onClick={onClick}>
                        {children}
                    </h5>
                );
            case "lg":
                return (
                    <h6 {...baseProps} className={`heading-lg ${typographyStyles}`} onClick={onClick}>
                        {children}
                    </h6>
                );
            default:
                return null;
        }
    } else if (variant === "body") {
        return (
            <p {...baseProps} className={`body-${size} ${typographyStyles}`} onClick={onClick}>
                {children}
            </p>
        );
    } else if (variant === "label") {
        return (
            <span {...baseProps} className={`label-${size} ${typographyStyles}`} onClick={onClick}>
                {children}
            </span>
        );
    }
    throw new Error("Typography component should not reach this case.");
};

export default Typography;
